import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { apiClient, formatApiError } from "../lib/api";
import { FEATURES } from "../lib/features";
import { Star, Loader2, ShieldCheck, AlertCircle } from "lucide-react";

/**
 * WriteReviewPage — buyer writes a review for a seller.
 *
 * Eligibility is enforced server-side (escrow completed OR closed dispute).
 * UI guides the user to pick one of their eligible transactions.
 */
export default function WriteReviewPage() {
  const { id: sellerId } = useParams();
  const { user } = useAuth();
  const { lang } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    rating: 0, title: "", body: "",
    escrow_payment_id: "", dispute_id: "", vehicle_id: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [eligible, setEligible] = useState({ escrows: [], disputes: [] });

  useEffect(() => {
    if (!user) return;
    // Best-effort: pre-populate eligible contexts so user doesn't paste IDs
    Promise.allSettled([
      apiClient.get("/disputes/mine"),
      apiClient.get("/escrow/mine").catch(() => ({ data: { items: [] } })),
    ]).then(([d, e]) => {
      const disputes = (d.value?.data?.items || []).filter(
        (x) => x.seller_id === sellerId && ["resolved", "closed", "auto_closed"].includes(x.status)
      );
      const escrows = (e.value?.data?.items || []).filter(
        (x) => x.seller_id === sellerId && ["released", "refunded", "partial_refund"].includes(x.status)
      );
      setEligible({ escrows, disputes });
    });
  }, [user, sellerId]);

  if (user === undefined) return <div className="p-10 text-[#525252]">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: `/sellers/${sellerId}/review` }} replace />;

  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const ctxSelected = form.escrow_payment_id || form.dispute_id;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await apiClient.post(`/sellers/${sellerId}/reviews`, {
        rating: form.rating,
        title: form.title.trim() || null,
        body: form.body.trim(),
        escrow_payment_id: form.escrow_payment_id || null,
        dispute_id: form.dispute_id || null,
        vehicle_id: form.vehicle_id || null,
        images: [],
      });
      navigate(`/sellers/${sellerId}`, { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 py-10" data-testid="write-review-page">
      <div className="overline overline-gold inline-flex items-center gap-2 mb-2">
        <Star className="w-3 h-3" /> {lang === "ar" ? "مراجعة جديدة" : "New review"}
      </div>
      <h1 className="font-display text-3xl tracking-[-0.02em] font-medium mb-2">
        {lang === "ar" ? "شارك تجربتك" : "Share your experience"}
      </h1>
      <p className="text-sm text-[#6b6b6b] mb-6">
        {FEATURES.ESCROW_ENABLED
          ? (lang === "ar"
              ? "المراجعات لا تُكتب إلا بعد إتمام صفقة موثقة عبر ضمان كارتال أو نزاع مغلق — لمنع المراجعات الوهمية."
              : "Reviews are only accepted after a verified CARTAL Escrow deal or a closed dispute — to prevent fake reviews.")
          : (lang === "ar"
              ? "المراجعات لا تُكتب إلا بعد إتمام صفقة موثقة أو نزاع مغلق — لمنع المراجعات الوهمية."
              : "Reviews are only accepted after a verified deal or a closed dispute — to prevent fake reviews.")}
      </p>

      {err && <div className="rounded-xl p-3 mb-4 bg-red-50 border border-red-200 text-red-700 text-sm">{String(err)}</div>}

      <form onSubmit={submit} className="surface rounded-2xl p-6 space-y-5">
        {/* Context picker */}
        <div>
          <label className="block text-xs font-semibold text-[#3a3a3a] mb-1.5">{lang === "ar" ? "اربط بصفقتك" : "Link to your transaction"}</label>
          {eligible.escrows.length === 0 && eligible.disputes.length === 0 ? (
            <p className="text-xs text-[#8a6f2e] bg-[#fef9ed] border border-[#c9a55c]/30 rounded-lg p-3 inline-flex items-start gap-1" data-testid="no-eligible-ctx">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              {lang === "ar"
                ? "ليس لديك صفقة Escrow مكتملة أو نزاع مغلق مع هذا البائع بعد. أكمل صفقة عبر ضمان كارتال أولاً."
                : "You don't have a completed Escrow or closed dispute with this seller yet."}
            </p>
          ) : (
            <select className="input-dark" value={ctxSelected ? `${form.escrow_payment_id ? "e:" + form.escrow_payment_id : "d:" + form.dispute_id}` : ""}
              onChange={(e) => {
                const v = e.target.value;
                if (v.startsWith("e:")) { upd("escrow_payment_id", v.slice(2)); upd("dispute_id", ""); }
                else if (v.startsWith("d:")) { upd("dispute_id", v.slice(2)); upd("escrow_payment_id", ""); }
              }}
              required data-testid="context-select"
            >
              <option value="">{lang === "ar" ? "— اختر —" : "— pick one —"}</option>
              {eligible.escrows.map((e) => (
                <option key={`e:${e.id}`} value={`e:${e.id}`}>
                  {lang === "ar" ? "ضمان كارتال" : "CARTAL Escrow"} · {e.id.slice(0, 8)} · {e.amount_sar} SAR
                </option>
              ))}
              {eligible.disputes.map((d) => (
                <option key={`d:${d.id}`} value={`d:${d.id}`}>
                  {lang === "ar" ? "نزاع مغلق" : "Closed dispute"} · {d.code}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Rating */}
        <div>
          <label className="block text-xs font-semibold text-[#3a3a3a] mb-1.5">{lang === "ar" ? "تقييمك" : "Your rating"}</label>
          <div className="flex gap-1" data-testid="rating-picker">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onClick={() => upd("rating", n)} className="p-1" data-testid={`rate-${n}`}>
                <Star className={`w-8 h-8 ${n <= form.rating ? "text-[#c9a55c] fill-[#c9a55c]" : "text-[#0a0a0a]/[0.15]"}`} />
              </button>
            ))}
          </div>
        </div>

        {/* Title (optional) */}
        <div>
          <label className="block text-xs font-semibold text-[#3a3a3a] mb-1.5">{lang === "ar" ? "عنوان (اختياري)" : "Title (optional)"}</label>
          <input className="input-dark" maxLength={120} value={form.title} onChange={(e) => upd("title", e.target.value)} data-testid="review-title" />
        </div>

        {/* Body */}
        <div>
          <label className="block text-xs font-semibold text-[#3a3a3a] mb-1.5">{lang === "ar" ? "تفاصيل تجربتك" : "Your experience"}</label>
          <textarea rows={5} className="input-dark" required minLength={10} maxLength={2000}
            value={form.body} onChange={(e) => upd("body", e.target.value)}
            placeholder={lang === "ar" ? "اشرح ما الذي جعل تجربتك جيدة أو سيئة بصدق وموضوعية." : "Be honest and helpful for the next buyer."}
            data-testid="review-body" />
        </div>

        <button type="submit" disabled={busy || !ctxSelected || !form.rating || form.body.trim().length < 10}
          className="btn-primary disabled:opacity-50" data-testid="review-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          {lang === "ar" ? "نشر المراجعة" : "Post review"}
        </button>
      </form>
    </div>
  );
}
